/* eslint-disable */
// ─── useReadingTracker ────────────────────────────────────────────────────────
// Tracks active reading time, scroll progress, idle state, tab visibility.
// Fires heartbeat every 60s. Checks auto-complete on every heartbeat.
// Queues failed saves offline and retries when back online.
//
// Usage:
//   useReadingTracker({ activeTopic, contentRef, onAutoComplete, saveFn })
//
// saveFn signature:
//   saveFn({ topicId, timeSpentSeconds, scrollPercent, status, recordedAt })

import { useEffect, useRef, useCallback } from 'react';
import { shouldAutoComplete, getScrollPercent } from './ebookUtils';
import { useOfflineQueue } from './useOfflineQueue';

const IDLE_THRESHOLD_MS  = 5 * 60 * 1000;  // 5 minutes
const HEARTBEAT_MS       = 60 * 1000;       // 60 seconds

export function useReadingTracker({ activeTopic, contentRef, onAutoComplete, saveFn }) {

  // ── Refs (no re-render needed) ──────────────────────────────────────────────
  const activeTopicRef      = useRef(null);   // current topic full object
  const activeTimeRef       = useRef(0);      // accumulated active seconds this topic
  const maxScrollRef        = useRef(0);      // highest scroll percent reached
  const sessionStartRef     = useRef(null);   // when current active segment started
  const isIdleRef           = useRef(false);  // is user currently idle
  const isHiddenRef         = useRef(false);  // is tab hidden
  const idleTimerRef        = useRef(null);   // setTimeout handle for idle
  const heartbeatTimerRef   = useRef(null);   // setInterval handle
  const autoCompletedRef    = useRef(false);  // prevent double-trigger

  const { enqueue, flush } = useOfflineQueue(saveFn);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  // Accumulate time from the current active segment into activeTimeRef
  const accumulateTime = useCallback(() => {
    if (sessionStartRef.current && !isIdleRef.current && !isHiddenRef.current) {
      const elapsed = Math.round((Date.now() - sessionStartRef.current) / 1000);
      activeTimeRef.current += elapsed;
    }
    sessionStartRef.current = null;
  }, []);

  // Resume the active time segment
  const resumeTimer = useCallback(() => {
    if (!isIdleRef.current && !isHiddenRef.current) {
      sessionStartRef.current = Date.now();
    }
  }, []);

  // Build the payload for a save
  const buildPayload = useCallback((status) => {
    const topic = activeTopicRef.current;
    if (!topic) return null;
    return {
      topicId:          topic.topicId,
      timeSpentSeconds: activeTimeRef.current,
      scrollPercent:    maxScrollRef.current,
      status:           status || topic.status || 'in_progress',
      recordedAt:       Date.now(),
    };
  }, []);

  // Save — try network first, enqueue on failure
  const save = useCallback(async (status) => {
    const payload = buildPayload(status);
    if (!payload) return;
    try {
      await saveFn(payload);
      await flush(); // try to flush any queued items while we're online
    } catch {
      enqueue(payload);
    }
  }, [buildPayload, saveFn, enqueue, flush]);

  // Check if auto-complete should trigger
  const checkAutoComplete = useCallback(() => {
    const topic = activeTopicRef.current;
    if (!topic || autoCompletedRef.current) return;
    if (topic.status === 'completed') return;

    const should = shouldAutoComplete(
      maxScrollRef.current,
      activeTimeRef.current,
      topic.readTimeMinutes || topic.read_time_minutes
    );

    if (should) {
      autoCompletedRef.current = true;
      save('completed');
      onAutoComplete && onAutoComplete(topic);
    }
  }, [save, onAutoComplete]);

  // ── Heartbeat ───────────────────────────────────────────────────────────────
  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatTimerRef.current = setInterval(() => {
      if (!isIdleRef.current && !isHiddenRef.current) {
        accumulateTime();
        resumeTimer();
        save();
        checkAutoComplete();
      }
    }, HEARTBEAT_MS);
  }, [accumulateTime, resumeTimer, save, checkAutoComplete]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  // ── Idle Detection ──────────────────────────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // If we were idle, resume now
    if (isIdleRef.current) {
      isIdleRef.current = false;
      resumeTimer();
    }

    idleTimerRef.current = setTimeout(() => {
      if (!isIdleRef.current) {
        isIdleRef.current = true;
        accumulateTime(); // save time accumulated before going idle
        save();           // heartbeat save when going idle
      }
    }, IDLE_THRESHOLD_MS);
  }, [accumulateTime, resumeTimer, save]);

  // ── Scroll Tracking ─────────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    resetIdleTimer();
    const el = contentRef?.current;
    if (!el) return;
    const pct = getScrollPercent(el);
    if (pct > maxScrollRef.current) {
      maxScrollRef.current = pct;
    }
  }, [contentRef, resetIdleTimer]);

  // ── Visibility Change ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        isHiddenRef.current = true;
        accumulateTime();
        save(); // save immediately when tab hides
      } else {
        isHiddenRef.current = false;
        resumeTimer();
        resetIdleTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [accumulateTime, resumeTimer, save, resetIdleTimer]);

  // ── User Activity Listeners ─────────────────────────────────────────────────
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
  }, [resetIdleTimer]);

  // ── Scroll Listener ─────────────────────────────────────────────────────────
  useEffect(() => {
    const el = contentRef?.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [contentRef, handleScroll, activeTopic]); // re-attach when topic changes

  // ── Topic Change ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeTopic) return;

    const isSameTopic = activeTopicRef.current?.topicId === activeTopic.topicId;
    if (isSameTopic) return;

    // Save previous topic before switching
    if (activeTopicRef.current) {
      accumulateTime();
      save();
    }

    // Reset all state for new topic
    activeTopicRef.current  = activeTopic;
    activeTimeRef.current   = activeTopic.timeSpentSeconds || 0; // resume from saved time
    maxScrollRef.current    = activeTopic.scrollPercent    || 0; // resume from saved scroll
    autoCompletedRef.current = activeTopic.status === 'completed';
    isIdleRef.current       = false;

    sessionStartRef.current = Date.now();

    startHeartbeat();
    resetIdleTimer();

    // Resume scroll position in content area
    const el = contentRef?.current;
    if (el && activeTopic.scrollPercent > 0) {
      const { scrollHeight, clientHeight } = el;
      const scrollable = scrollHeight - clientHeight;
      if (scrollable > 0) {
        el.scrollTop = (activeTopic.scrollPercent / 100) * scrollable;
      }
    }

    return () => {
      // Cleanup when topic changes (handled by next effect run)
    };
  }, [activeTopic?.topicId]); // only re-run when topicId changes

  // ── Cleanup on Unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      accumulateTime();
      save();
      stopHeartbeat();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  // ── Expose manual save for mark-complete button ─────────────────────────────
  const saveNow = useCallback((status) => {
    accumulateTime();
    resumeTimer();
    return save(status);
  }, [accumulateTime, resumeTimer, save]);

  return { saveNow };
}